import { useEffect, useState } from "react";
import type { Role, TeamEntry, User } from "@board/shared";
import { api, ApiError } from "./api";
import { useBoard } from "./store";

const STATUS_LABEL = { pending: "Invited", active: "Active", disabled: "Disabled" } as const;

export function TeamManager({ me, onDone }: { me: User; onDone: () => void }) {
  const reloadBoard = useBoard((state) => state.load);
  const [people, setPeople] = useState<TeamEntry[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    api.team.list()
      .then((result) => setPeople(result.people))
      .catch(() => setProblem("Couldn’t load the team."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDone();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onDone]);

  async function invite() {
    const value = email.trim().toLowerCase();
    if (!value) return;
    setSaving("invite");
    setProblem(null);
    try {
      const result = await api.team.invite({ email: value, role });
      setPeople(result.people);
      setEmail("");
      setRole("member");
      await reloadBoard();
    } catch (error) {
      setProblem(error instanceof ApiError && error.code === "not_permitted"
        ? "You can’t change your own access or role."
        : "Couldn’t add that person.");
    } finally {
      setSaving(null);
    }
  }

  async function update(person: TeamEntry, patch: { role?: Role; disabled?: boolean }) {
    if (!person.user_id) return;
    setSaving(person.email);
    setProblem(null);
    try {
      const result = await api.team.update(person.user_id, patch);
      setPeople(result.people);
      await reloadBoard();
    } catch (error) {
      setProblem(error instanceof ApiError && error.code === "not_permitted"
        ? "You can’t change your own access or role."
        : "That change didn’t save.");
    } finally {
      setSaving(null);
    }
  }

  async function revoke(person: TeamEntry) {
    setSaving(person.email);
    setProblem(null);
    try {
      await api.team.revoke(person.email);
      setPeople((current) => current.filter((entry) => entry.email !== person.email));
    } catch {
      setProblem("That invitation couldn’t be removed.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="teambackdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onDone();
    }}>
      <section className="teampanel" role="dialog" aria-modal="true" aria-labelledby="team-title">
        <header className="teampanel__head">
          <div>
            <p className="teampanel__eyebrow">smrt board</p>
            <h2 id="team-title">People</h2>
            <p>Invite people with their Google email. They become active after their first sign-in.</p>
          </div>
          <button className="teampanel__close" onClick={onDone} aria-label="Close people">×</button>
        </header>

        <form className="teaminvite" onSubmit={(event) => { event.preventDefault(); void invite(); }}>
          <label>
            <span>Email</span>
            <input type="email" required value={email} placeholder="person@company.com"
              onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label>
            <span>Role</span>
            <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button className="teaminvite__button" disabled={saving === "invite"}>
            {saving === "invite" ? "Adding…" : "Add person"}
          </button>
        </form>

        {problem ? <p className="teampanel__problem" role="alert">{problem}</p> : null}

        <div className="teamlist" aria-busy={loading}>
          {loading ? <p className="teamlist__empty">Loading people…</p> : null}
          {!loading && people.length === 0 ? <p className="teamlist__empty">No one has been invited yet.</p> : null}
          {people.map((person) => {
            const isMe = person.user_id === me.id;
            const busy = saving === person.email;
            return (
              <article className={`person person--${person.status}`} key={person.email}>
                <div className="person__avatar" aria-hidden="true">
                  {person.avatar_url ? <img src={person.avatar_url} alt="" /> : person.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="person__identity">
                  <strong>{person.name}{isMe ? " (you)" : ""}</strong>
                  <span>{person.email}</span>
                </div>
                <span className={`person__status person__status--${person.status}`}>
                  {STATUS_LABEL[person.status]}
                </span>
                {person.status === "pending" ? (
                  <>
                    <span className="person__role">{person.role}</span>
                    <button className="person__action" disabled={busy} onClick={() => void revoke(person)}>Remove</button>
                  </>
                ) : (
                  <>
                    <select
                      className="person__rolepick"
                      aria-label={`Role for ${person.name}`}
                      value={person.role}
                      disabled={isMe || busy}
                      onChange={(event) => void update(person, { role: event.target.value as Role })}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      className="person__action"
                      disabled={isMe || busy}
                      onClick={() => void update(person, { disabled: person.status !== "disabled" })}
                    >
                      {person.status === "disabled" ? "Reactivate" : "Disable"}
                    </button>
                  </>
                )}
              </article>
            );
          })}
        </div>

        <footer className="teampanel__foot">
          <span>Board link</span>
          <code>https://board.smrt.ist</code>
          <button onClick={() => void navigator.clipboard.writeText("https://board.smrt.ist")}>Copy</button>
        </footer>
      </section>
    </div>
  );
}
